import { ActivatedRoute, Router } from '@angular/router';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { ETableColumnType, ITableColumn, ITableDataSource, DataSourceParams } from 'app/store/models/table.model';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { LeftMenuService } from 'app/store/services/left-menu.service';
import { Store } from '@ngrx/store';
import { IReducers } from 'app/store/reducers';
import { SessionService } from 'app/store/services/session.service';
import { ApiDataSource } from 'app/datasources/api.datasrouce';
import { takeUntil } from 'rxjs/operators';
import { setMessagesList } from 'app/store/actions/message.action';
import { HttpClient } from '@angular/common/http';
import { ISelectItem } from 'app/store/models/select.model';
import { IAvailable } from 'app/store/models/available.model';
import { IUserSessionData, UserType } from 'app/store/models/user.model';
import { environment } from '../../../environments/environment';
import { ContactsService } from 'app/store/services/contacts.service';
import { openSnackbar } from 'app/store/actions/page.action';
import { updateLeftMenuCount } from 'app/store/actions/meeting.action';

@Component({
  selector: 'app-available-listing',
  templateUrl: './available-listing.component.html',
  styleUrls: ['./available-listing.component.scss'],
})
export class AvailableListingComponent implements OnInit, OnDestroy {
  @Input() type;
  tableDataSourceParams: DataSourceParams = {
    page: 1,
    pageSize: 10,
    sortAttr: 'action_data1',
    sortDir: 'desc',
    filters: {},
  };
  tableColumns: ITableColumn<IAvailable>[];
  dataSource: ApiDataSource<IAvailable>;
  exportButtonProperties: Button = {
    onClick: () => {},
    size: ButtonSize.SMALL,
    text: 'Export',
    color: ButtonColor.ORANGE,
  };
  tableReady: boolean;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private http: HttpClient,
    private router: Router,
    private leftMenu: LeftMenuService,
    private store: Store<IReducers>,
    private sessionService: SessionService,
    private activeRoute: ActivatedRoute,
    private contactsService: ContactsService,
  ) {
    this.dataSource = new ApiDataSource<IAvailable>(
      this.http,
      environment.apiUrl + '/v1/messages/available',
    );
    this.leftMenu.expand('inbox');
  }

  rowClass = () => ['route_message'];

  ngOnInit() {
    this.type = this.activeRoute.snapshot.data.type;
    this.tableDataSourceParams = {
      page: 1,
      pageSize: 10,
      sortAttr: 'action_data1',
      sortDir: 'desc',
      filters: {
        type: this.type,
      },
    };

    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.tableColumns = this.getColumns(sessionData);
        this.tableReady = true;
      });
  }

  ngOnDestroy() {
    this.destroy$.next(false);
  }

  onColumnClick(item: IAvailable) {
    this.router.navigate(['/messages/view', item.message_id]);
  }

  onData = (items: IAvailable[]) => {
    if (Array.isArray(items) && items.length) {
      this.store.dispatch(
        setMessagesList({ list: items.map(item => item.message_id) }),
      );
    } else {
      this.store.dispatch(setMessagesList({ list: [] }));
    }
  };

  private getColumns(
    sessionData: IUserSessionData,
  ): ITableColumn<IAvailable>[] {
    switch (this.type) {
      case 'available':
      case 'progress':
      case 'placed':
      case 'delete':
        return this.getAvailableColumns();
      case 'future':
        return this.getFeatureAvailableColumns(sessionData);
      case 'unavailable':
        return this.getNotAvailableColumns();
    }
  }

  private getFeatureAvailableColumns(
    sessionData: IUserSessionData,
  ): ITableColumn<IAvailable>[] {
    const columns: ITableColumn<IAvailable>[] = [
      {
        id: 'contact_firstname',
        title: 'Contact',
        sortable: true,
        renderer: row => {
          return `${row.contact_firstname ? row.contact_firstname : ''} ${
            row.contact_lastname ? row.contact_lastname : ''
          }`;
        },
        route: row => this.onColumnClick(row),
      },
      {
        id: 'contact_company',
        title: 'Company',
        sortable: true,
        route: row => this.onColumnClick(row),
      },
      {
        id: 'action_name',
        title: 'Cycle',
        renderer: row => `${row.action_name} months`, // TODO: check if this is doing inlline action
        sortable: true,
        route: row => this.onColumnClick(row),
      },
      {
        id: 'action_data',
        title: 'Scheduled Cycle Date',
        sortable: true,
        route: row => this.onColumnClick(row),
      }];


    if (sessionData.user_type !== UserType.SINGLE_USER) {
      columns.push({
        id: 'account_firstname',
        title:
          sessionData.user_type === UserType.MASTER
            ? 'Account'
            : 'Representative',
        sortable: true,
        renderer: row => {
          return `${row.account_firstname} ${row.account_lastname}`;
        },
        route: row => this.onColumnClick(row),
      });
    }

    if (sessionData.user_type === UserType.MASTER) {
      columns.push({
        id: 'follow_reply',
        title: 'Indicator',
        type: ETableColumnType.CHECKBOX,
        checkboxProperties: {
          label: 'Replay',
          disabled: true,
        },
      });
    }

    columns.push({
      id: 'view_form',
      type: ETableColumnType.BUTTON,
      buttonProperties: {
        color: ButtonColor.ORANGE,
        text: 'View Form',
        size: ButtonSize.SMALL,
        onClick: (item: IAvailable) =>
          window.open(item.recruit_link, '_blank'),
      },
    });

    return columns;
  }

  private getNotAvailableColumns(): ITableColumn<IAvailable>[] {
    const columns: ITableColumn<IAvailable>[] = [
      {
        id: 'contact_firstname',
        title: 'Contact',
        sortable: true,
        renderer: row => {
          return `${row.contact_firstname ? row.contact_firstname : ''} ${
            row.contact_lastname ? row.contact_lastname : ''
          }`;
        },
        route: row => this.onColumnClick(row),
      },
      {
        id: 'contact_email',
        title: 'Email',
        sortable: true,
        route: row => this.onColumnClick(row),
      },
      {
        id: 'message_created',
        title: 'Date',
        sortable: true,
        route: row => this.onColumnClick(row),
      },
      {
        id: 'reason',
        title: 'Reason',
        sortable: true,
        route: row => this.onColumnClick(row),
      },
      {
        id: 'view_form',
        type: ETableColumnType.BUTTON,
        buttonProperties: {
          color: ButtonColor.ORANGE,
          text: 'View Form',
          size: ButtonSize.SMALL,
          onClick: (item: IAvailable) =>
            window.open(item.recruit_link, '_blank'),
        },
      },
    ];

    return columns;
  }

  private getAvailableColumns(): ITableColumn<IAvailable>[] {
    const avaiableActions = [
      {
        id: 'progress',
        label: 'In progress',
      },
      {
        id: 'placed',
        label: 'Placed',
      },
      {
        id: 'delete',
        label: 'Deleted',
      },
    ];

    return [
      {
        id: 'actions',
        type: ETableColumnType.SELECT,
        selectProperties: {
          label: 'Move to',
          resetSelection: true,
          onSelection: (value, table, element) => this.handleMoveTo(value, element),
          buildItems: () => {
            const items: ISelectItem[] = [];
            avaiableActions
              .filter(item => item.id !== this.type)
              .forEach(item =>
                items.push({
                  label: item.label,
                  value: item.id,
                }),
              );

            return items;
          },
        },
      },
      {
        id: 'contact_firstname',
        title: 'Contact',
        sortable: true,
        renderer: row => {
          return `${row.contact_firstname ? row.contact_firstname : ''} ${
            row.contact_lastname ? row.contact_lastname : ''
          }`;
        },
        route: row => this.onColumnClick(row),
      },
      {
        id: 'contact_email',
        title: 'Email',
        sortable: true,
        route: row => this.onColumnClick(row),
      },
      {
        id: 'updated_at',
        title: 'Date',
        sortable: true,
        route: row => this.onColumnClick(row),
      },
      {
        id: 'view_form',
        type: ETableColumnType.BUTTON,
        buttonProperties: {
          color: ButtonColor.ORANGE,
          text: 'View Form',
          size: ButtonSize.SMALL,
          onClick: (item: IAvailable) =>
            window.open(item.recruit_link, '_blank'),
        },
      },
    ];
  }

  private handleMoveTo(value: string, element: IAvailable) {
    const mapping = {
      progress: 1,
      placed: 2,
      delete: 3,
    } as const;

    const workflowState = mapping[value];
    if (!workflowState || !element?.contact_id) {
      return;
    }

    this.contactsService.quickResponse(
      {
        contact_id: element.contact_id,
        workflow_state: workflowState,
        type: 'available',
      },
      'available',
    ).subscribe(
      () => {
        this.dataSource.refresh();
        this.store.dispatch(updateLeftMenuCount({ data: {} }));
      },
      () => {
        this.store.dispatch(openSnackbar({
          snackbarData: {
            data: {
              message: 'Failed to update contact status. Please try again.',
              type: 'error',
            },
          },
        }));
      },
    );
  }
}
